import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { observable, computed, action, reaction, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { TimeRegistration, combineDateAndTime } from 'store/TimeRegistration';
import { combinedStore } from 'store/Base';
import { Subproject, SubprojectStore, MILESTONE_STATUS_PLANNING, MILESTONE_STATUS_ACTIVE, MILESTONE_STATUS_COMPLETED, MILESTONE_STATUS_ON_HOLD } from 'store/Subproject';
import { Ticket, TicketStore } from 'store/Ticket';
import { showSaveNotification } from '@code-yellow/spider';
import { TargetTextInput, TargetSelect, TargetDatePicker, TargetTimePicker, momentToLuxon, luxonToMoment } from '@code-yellow/spider';
import { Form, Button, Icon, Label } from 'semantic-ui-react';
import { DATE_FORMAT, TIME_FORMAT } from 'helpers';
import styled, { injectGlobal } from 'styled-components';

const CenterIconButton = styled(Button)`
    align-self: flex-end;
    height: calc(1px * 2 + 0.67857143em * 2 + 1.21428571em) !important;
    margin: 0 0.5em !important;
`;

const FlexGroup = styled.div`
    display: flex;
    flex: 1 1 auto;
    //margin-left: 0.5em;
    gap: 1em;
    flex-wrap: wrap;

`

const StyledTargetSelect = styled(TargetSelect)`
    div:first-of-type{
        max-width: 375px !important;
        min-width: 160px !important;
        width: 250px;
    }

    ${props => props.noLabel && `
    .dropdown:not(.active){
        border: None !important;
        background: None !important;
        padding-left: none !important;
        padding-right: none !important;
        padding-top: none !important;
        padding-bottom: none !important;
        margin-right: -30px !important;
        
        
        i.dropdown.icon {
        display: none !important;
        }
    }
    
    `}
    

`;

function momentToLuxonNullSafe(value) {
    if (value === null) {
        return null;
    }
    return momentToLuxon(value);
}

function luxonToMomentNullSafe(value) {
    if (value === null) {
        return null;
    }
    return luxonToMoment(value);
}

// [T53675 feedback] The labels in the picker / quick bar show only the
// customer and the sub project: the project segment made them too long
// (and sub project names can contain " - " themselves, so full_name
// cannot just be trimmed client side). The display_name annotation
// carries the compact label; full_name (Company - Project - Sub project)
// stays for searching and hover context. The annotation is not hydrated
// on sub projects that come from a ticket relation (nested with); fall
// back to company - name from the relation data (mirrors the annotation).
export function subprojectLabel(subproject) {
    if (subproject.displayName) {
        return subproject.displayName;
    }
    const project = subproject.project;
    if (project) {
        const company = project.company;
        if (company && (company.shortName || company.name)) {
            return `${company.shortName || company.name} - ${subproject.name}`;
        }
        return `${project.name} - ${subproject.name}`;
    }
    return subproject.name;
}

// What a combined picker option shows: the sub project or the ticket.
function combinedOptionLabel(subprojectOrTicket) {
    if (!subprojectOrTicket.subproject.isNew) {
        return subprojectLabel(subprojectOrTicket.subproject);
    }
    return subprojectOrTicket.ticket.fullName;
}

// [T53675 follow-up] Picker options carry a colored left border instead
// of a status text on every row: sub projects get their status color
// (colors mirror the planning timeline, so a status has the same color
// everywhere) and tickets a neutral grey border, making sub projects and
// tickets visually distinct at a glance. Tickets linked to a sub project
// carry a small dot in that sub project's status color. The sub project
// status is still available on hover via the option title. Completed
// sub projects are not offered at all (see the picker fetch params): the
// border mainly distinguishes the workable ones, and only surfaces for a
// completed sub project that is already on an entry (history).
const SUBPROJECT_STATUS_COLORS = {
    [MILESTONE_STATUS_PLANNING]: '#805ad5',
    [MILESTONE_STATUS_ACTIVE]: '#3182ce',
    [MILESTONE_STATUS_COMPLETED]: '#38a169',
    [MILESTONE_STATUS_ON_HOLD]: '#dd6b20',
};

// SUI spreads option props onto the DropdownItem, so the border is a
// plain class on the dropdown item itself. A styled-components `content`
// element would lose its generated class: SUI rebuilds the element from
// its raw props.
const OPTION_BORDER_CLASS = 'tr-picker-option';

// A ticket linked to a sub project carries a small dot in that sub
// project's status color, so it is visible that time on the ticket will
// be attributed to a sub project (which one is on the option title; the
// sub project itself is auto-selected on pick). Ticket names are never
// shortened. Also a plain class for the same reason — only the plain
// element tree of option `content` survives SUI's option processing.
const TICKET_SUBPROJECT_DOT_CLASS = 'tr-ticket-subproject-dot';

injectGlobal`
    .ui.dropdown .menu > .item.${OPTION_BORDER_CLASS} {
        border-left: 3px solid rgba(0, 0, 0, 0.4);
    }
    ${Object.entries(SUBPROJECT_STATUS_COLORS).map(([status, color]) => (`
        .ui.dropdown .menu > .item.${OPTION_BORDER_CLASS}--${status} {
            border-left-color: ${color};
        }
    `)).join('')}
    .${TICKET_SUBPROJECT_DOT_CLASS} {
        display: inline-block;
        width: 0.5em;
        height: 0.5em;
        border-radius: 50%;
        margin-left: 0.5em;
        vertical-align: middle;
    }
`;

// Extra option props (spread into `toOption` results): the border class
// plus the full text as hover title — for sub projects with their
// status.
export function subprojectOptionExtras(subproject) {
    const status = subproject.status;
    const label = subprojectLabel(subproject);
    return {
        className: status ? `${OPTION_BORDER_CLASS} ${OPTION_BORDER_CLASS}--${status}` : OPTION_BORDER_CLASS,
        title: status ? `${label} (${t(`subproject.field.status.options.${status}`)})` : label,
    };
}

function ticketOptionExtras(ticket) {
    const subproject = ticket.subproject;
    return {
        className: OPTION_BORDER_CLASS,
        title: subproject && subproject.id !== null ? `${ticket.fullName} (${subprojectLabel(subproject)})` : undefined,
    };
}

// The dot for a ticket with a sub project, in the sub project's status
// color; tickets without one keep their plain option text.
export function ticketOptionContent(ticket) {
    const subproject = ticket.subproject;
    if (subproject && subproject.id !== null) {
        return (
            <span>
                {ticket.fullName}
                <span
                    className={TICKET_SUBPROJECT_DOT_CLASS}
                    style={{ backgroundColor: SUBPROJECT_STATUS_COLORS[subproject.status] || 'rgba(0, 0, 0, 0.4)' }}
                />
            </span>
        );
    }
    return undefined;
}

function combinedOptionExtras(subprojectOrTicket) {
    if (subprojectOrTicket.subproject.isNew) {
        return ticketOptionExtras(subprojectOrTicket.ticket);
    }
    return subprojectOptionExtras(subprojectOrTicket.subproject);
}

function combinedOptionContent(subprojectOrTicket) {
    if (subprojectOrTicket.subproject.isNew) {
        return ticketOptionContent(subprojectOrTicket.ticket);
    }
    return undefined;
}

// [T53675] Time is registered on sub projects (a sub project always
// belongs to a project) — never on a project itself: internal work lives
// on sub projects of internal projects (e.g. Code Yellow -> Realtime).
// Tickets are offered alongside them. The `projects` m2m on the time
// registration is derived from the picked sub projects so all project
// totals, filters, exports and Metabase dashboards keep working.
// [T53675 follow-up] Project is dropped from the combined model entirely:
// it was only kept so legacy entries could display their manually
// registered projects in the picker. Those projects stay on the entry in
// the backend (the admin overview still shows them), but the picker offers
// and displays sub projects and tickets only. Options are ordered purely
// by relevance (recently used first), across both models.
export const SubprojectOrTicketStore = combinedStore(Subproject, Ticket);

const ViewIcon = styled(Icon)`
    margin-left: 0.25em !important;
    margin-right: 0 !important;
    color: rgba(0, 0, 0, 0.6) !important;
    &:hover {
        color: rgba(0, 0, 0, 0.8) !important;
    }
`;

@observer
export class TicketLabel extends Component {
    static propTypes = {
        number: PropTypes.number.isRequired,
    };

    onClick(e) {
        e.stopPropagation();
    }

    render() {
        const { number } = this.props;
        return (
            <>
                T{number}
                <a target="_blank" href={`https://phabricator.codeyellow.nl/T${number}`}>
                    <ViewIcon name="eye" onClick={this.onClick} />
                </a>
            </>
        );
    }
}

@observer
export class SubprojectsOrTicketsSelect extends Component {
    static propTypes = {
        timeRegistration: PropTypes.instanceOf(TimeRegistration).isRequired,
        subprojectOrTicketStore: PropTypes.instanceOf(SubprojectOrTicketStore).isRequired,
    };

    @observable ticketStore = new TicketStore({ relations: ['subproject.project.company'] })
    // Separate selects mode: completed sub projects are not offered for
    // new entries either. Sub projects already on the entry are pushed
    // into this store client side (see componentDidMount), so history
    // keeps displaying them.
    @observable subprojectStore = new SubprojectStore({ relations: ['project.company'], params: { '.project.hidden': false, '.status:not:in': 'completed' } })
    stopReactions = []

    constructor(...args) {
        super(...args);
        this.setSubprojectsOrTickets = this.setSubprojectsOrTickets.bind(this);
    }

    componentDidMount(){
        const { timeRegistration } = this.props

        if (window.viewStore.currentUser.separateProjectTicketSelects) {
            // We have to make sure that the sub projects added to the time registration exist in the store so that we can show them
            this.stopReactions.push(reaction(
                () => timeRegistration.subprojects.models.length,
                () => timeRegistration.subprojects.models.forEach(subproject => {
                    if (!this.subprojectStore.get(subproject.id)) {
                        this.subprojectStore.models.push(subproject)
                        if (!subproject.displayName) {
                            subproject.fetch()
                        }
                    }
                }),
                { fireImmediately: true })
            )

            // We have to make sure that the tickets added to the time registration exist in the store so that we can show them
            this.stopReactions.push(reaction(
                () => timeRegistration.tickets.models.length,
                () => {
                    timeRegistration.tickets.models.forEach(ticket => {
                        if (!this.ticketStore.get(ticket.id)) {
                            this.ticketStore.models.push(ticket);
                            if (!ticket.fullName){
                                ticket.fetch()
                            }
                        }
                    })
                },
                { fireImmediately: true })
            )
        }

    }

    componentWillUnmount() {
        if (window.viewStore.currentUser.separateProjectTicketSelects) {
            this.stopReactions.forEach(stopReaction => stopReaction())
        }
    }


    @computed get subprojectsOrTickets() {
        const { timeRegistration } = this.props;
        const store = new SubprojectOrTicketStore({
            relations: ['subproject.project.company', 'ticket.subproject.project.company'],
        });

        // eslint-disable-next-line no-unused-vars
        for (const subproject of timeRegistration.subprojects.models) {
            store.add({
                id: SubprojectOrTicketStore.getId(subproject),
                subproject: subproject.toJS(),
            });
        }
        // Projects are not part of the picker anymore (time is clocked on
        // sub projects). Legacy entries that still carry manually
        // registered projects keep them in the backend — they are not
        // displayed here (the admin overview still shows them).
        // eslint-disable-next-line no-unused-vars
        for (const ticket of timeRegistration.tickets.models) {
            store.add({
                id: SubprojectOrTicketStore.getId(ticket),
                ticket: ticket.toJS(),
            });
        }

        return store;
    }

    /**
     * Resolve a combined picker id (see `toOption`) to its model. Newly
     * picked options live in the fetched store; options that are already
     * selected but are no longer in the fetched store (e.g. filtered out
     * by a search) live in the value store.
     */
    resolveSubprojectOrTicket(id) {
        const { subprojectOrTicketStore } = this.props;
        return subprojectOrTicketStore.get(id) || this.subprojectsOrTickets.get(id) || null;
    }

    @action setSubprojectsOrTickets(subprojectOrTicketIds) {
        const { timeRegistration } = this.props;

        // Spider passes the raw option values: the combined ids. The clear
        // button passes null.
        const subprojectOrTickets = (subprojectOrTicketIds || [])
            .map((id) => this.resolveSubprojectOrTicket(id))
            .filter((model) => model !== null);

        const subprojects = [];
        const tickets = [];

        // eslint-disable-next-line no-unused-vars
        for (const subprojectOrTicket of subprojectOrTickets) {
            const model = subprojectOrTicket;
            if (model.subproject && !model.subproject.isNew) {
                subprojects.push(model.subproject);
            } else {
                tickets.push(model.ticket);
            }
        }

        // Registering time on a ticket with a sub project automatically
        // selects that sub project (in a later stage Phabricator project
        // tags will keep ticket.subproject filled in).
        // eslint-disable-next-line no-unused-vars
        for (const ticket of tickets) {
            const subproject = ticket.subproject;
            if (subproject && subproject.id !== null && !subprojects.some((existing) => existing.id === subproject.id)) {
                subprojects.push(subproject);
            }
        }

        timeRegistration.setInput('subprojects', subprojects);
        timeRegistration.setInput('tickets', tickets);

        // The projects m2m is derived from what was picked, so project
        // totals, filters and exports keep working. Projects cannot be
        // picked anymore, so the derivation is a union with the projects
        // already on the entry: legacy manually registered projects are
        // preserved. When no sub projects are picked the projects are left
        // as they were — legacy entries keep their manually registered
        // projects, new ones stay empty and are indicated as incomplete.
        if (subprojects.length > 0) {
            const derived = [];
            // eslint-disable-next-line no-unused-vars
            for (const subproject of subprojects) {
                if (subproject.project && !derived.some((project) => project.id === subproject.project.id)) {
                    derived.push(subproject.project);
                }
            }
            // eslint-disable-next-line no-unused-vars
            for (const project of timeRegistration.projects.models) {
                if (!derived.some((existing) => existing.id === project.id)) {
                    derived.push(project);
                }
            }
            timeRegistration.setInput('projects', derived);
        }
    }

    /**
     * [T53675] Auto select the sub project of the tickets on the time
     * registration (used by the separate selects mode) and derive the
     * projects from the sub projects.
     */
    @action.bound
    syncSubprojectsAndProjects() {
        const { timeRegistration } = this.props;

        const subprojects = [...timeRegistration.subprojects.models];
        // eslint-disable-next-line no-unused-vars
        for (const ticket of timeRegistration.tickets.models) {
            const subproject = ticket.subproject;
            if (subproject && subproject.id !== null && !subprojects.some((existing) => existing.id === subproject.id)) {
                subprojects.push(subproject);
            }
        }
        if (subprojects.length !== timeRegistration.subprojects.models.length) {
            timeRegistration.setInput('subprojects', subprojects);
        }

        if (subprojects.length > 0) {
            // Projects cannot be picked directly anymore, so the
            // derivation is a union with the projects already on the
            // entry: derived projects are kept in sync, legacy manually
            // registered ones are preserved.
            const derived = [];
            // eslint-disable-next-line no-unused-vars
            for (const subproject of subprojects) {
                if (subproject.project && !derived.some((project) => project.id === subproject.project.id)) {
                    derived.push(subproject.project);
                }
            }
            // eslint-disable-next-line no-unused-vars
            for (const project of timeRegistration.projects.models) {
                if (!derived.some((existing) => existing.id === project.id)) {
                    derived.push(project);
                }
            }
            timeRegistration.setInput('projects', derived);
        }
    }

    render() {
        const { timeRegistration, subprojectOrTicketStore, ...props } = this.props;
        if (!subprojectOrTicketStore){
            return
        }

        // Render separate selects if user setting is enabled
        if (window.viewStore.currentUser.separateProjectTicketSelects) {
            return (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5em' }}>
                    <StyledTargetSelect search remote multiple skipFetch
                        target={timeRegistration}
                        name="subprojects"
                        store={this.subprojectStore}
                        toOption={subproject => ({
                            value: subproject.id,
                            text: subprojectLabel(subproject),
                            ...subprojectOptionExtras(subproject),
                        })}
                        afterChange={this.syncSubprojectsAndProjects}
                        {...props}
                        placeholder={t('timeRegistration.field.subprojects.label')}
                    />
                    <StyledTargetSelect search remote multiple skipFetch
                        target={timeRegistration}
                        name="tickets"
                        store={this.ticketStore}
                        toOption={ticket => ({
                            value: ticket.id,
                            text: ticket.fullName,
                            ...ticketOptionExtras(ticket),
                            content: ticketOptionContent(ticket),
                        })}
                        afterChange={this.syncSubprojectsAndProjects}
                        renderLabel={({ value, ...labelProps }) => {
                            let ticket = this.ticketStore.get(value)
                            labelProps.content = <TicketLabel number={ticket?.number}/>
                            return <Label {...labelProps} />
                        }}
                        {...props}
                        placeholder={t('timeRegistration.field.tickets.label')}
                    />
                </div>
            );
        }

        // Render combined select (existing behavior)
        return (
            <TargetSelect search remote multiple skipFetch
                target={timeRegistration}
                name="subprojectsOrTickets"
                value={this.subprojectsOrTickets}
                onChange={this.setSubprojectsOrTickets}
                store={subprojectOrTicketStore}
                fromTarget={(store) => store.map((model) => model.id)}
                toOption={subprojectOrTicket => ({
                    value: subprojectOrTicket.id,
                    text: combinedOptionLabel(subprojectOrTicket),
                    ...combinedOptionExtras(subprojectOrTicket),
                    content: combinedOptionContent(subprojectOrTicket),
                })}
                renderLabel={({ value, ...labelProps }) => {
                    const subprojectOrTicket = this.resolveSubprojectOrTicket(value);
                    if (!subprojectOrTicket) {
                        return null;
                    } else if (!subprojectOrTicket.subproject.isNew) {
                        labelProps.content = subprojectLabel(subprojectOrTicket.subproject);
                    } else {
                        labelProps.content = <TicketLabel number={subprojectOrTicket.ticket.number} />;
                    }
                    return <Label {...labelProps} />;
                }}
                {...props}
            />
        );
    }
}

@observer
export default class TimeRegistrationForm extends Component {
    static propTypes = {
        timeRegistration: PropTypes.instanceOf(TimeRegistration).isRequired,
        autosave: PropTypes.bool,
        group: PropTypes.bool,
        noLabel: PropTypes.bool,
        children: PropTypes.node,
        startPlaceholder: PropTypes.object,
        endPlaceholder: PropTypes.object,
        renderFlex: PropTypes.bool,
    };

    static defaultProps = {
        autosave: false,
        group: false,
        noLabel: false,
        children: null,
        startPlaceholder: null,
        endPlaceholder: null,
    }

    constructor(...args) {
        super(...args);
        this.setDate = this.setDate.bind(this);
        this.setStartedAt = this.setStartedAt.bind(this);
        this.onSubprojectsOrTicketsKeyDown = this.onSubprojectsOrTicketsKeyDown.bind(this);
    }

    @observable subprojectOrTicketStore = new SubprojectOrTicketStore({
            relations: ['subproject.project.company', 'ticket.subproject.project.company'],
            params: {
                // Relevance (recently used first) orders the picker options
                // across sub projects and tickets; model_index breaks ties.
                order_by: '-relevance,model_index',
                '.subproject.project.hidden': 'false',
                // [T53675 follow-up] Completed sub projects cannot be
                // picked for new entries. Entries that already use them
                // keep showing them: the picker merges the entry's own
                // sub projects into the options.
                '.subproject.status:not:in': 'completed',
                // We have to do this weird include annotations thing, otherwise the combined view complains that it cannot find the relevant annotation
                include_annotations: 'subproject:.*,ticket:.*'
            },
        });

    save = async () => {
        const { timeRegistration } = this.props;
        // _saveAll so that we do not process the response data, we do not want
        // this since the websocket data tends to come earlier than the
        // response data and it can be that based on that we decide to clear
        // the time registration and start a new one
        await timeRegistration.api[timeRegistration.isNew ? 'post' : 'patch'](
            timeRegistration.url,
            timeRegistration.toBackend({ onlyChanges: true }),
        );
        showSaveNotification();
    }

    autosaveIfDescriptionChanged = () => {
        const { timeRegistration } = this.props;
        if (timeRegistration.__changes.includes('description')) {
            this.autosave();
        }
    };

    autosave = () => {
        const { timeRegistration, autosave } = this.props;
        if (autosave && timeRegistration.startedAt !== null) {
            this.save();
        }
    };

    @observable startDate = null;

    @action setDate(startedAt) {
        const { timeRegistration } = this.props;

        if (startedAt !== null && timeRegistration.startedAt === null) {
            this.startDate = startedAt;
            const form = ReactDOM.findDOMNode(this);
            const timeInput = form.querySelector('.time-picker > input[name="startedAt"]');
            timeInput.select();
        } else {
            timeRegistration.setInput('startedAt', startedAt);
            this.autosave();
        }
    }

    @action setStartedAt(startedAt) {
        const { timeRegistration } = this.props;

        if (startedAt !== null && timeRegistration.endedAt === null) {
            const form = ReactDOM.findDOMNode(this);
            const endInput = form.querySelector('input[name="endedAt"]');
            endInput.select();
        }

        if (this.startDate && startedAt !== null) {
            startedAt = combineDateAndTime(this.startDate, startedAt);
            this.startDate = null;
        }
        timeRegistration.setInput('startedAt', startedAt);
        this.autosave();
    }

    onSubprojectsOrTicketsKeyDown(e) {
        if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            const form = ReactDOM.findDOMNode(this);
            const descriptionInput = form.querySelector('input[name="description"]');
            descriptionInput.select();
        }
    }

    componentDidMount(){
        this.subprojectOrTicketStore.fetch()
    }

    getSettings() {
        const { timeRegistration, noLabel, startPlaceholder, endPlaceholder } = this.props;
        return [
            {
                Component: SubprojectsOrTicketsSelect,
                subprojectOrTicketStore: this.subprojectOrTicketStore,
                noLabel: noLabel,
                placeholder: noLabel ? t('timeRegistration.field.subprojectsOrTickets.label') : undefined,
                timeRegistration: timeRegistration,
                afterChange: this.autosave,
                autoComplete: 'off',
                onKeyDown: this.onSubprojectsOrTicketsKeyDown,
            },
            {
                Component: TargetTextInput,
                noLabel: noLabel,
                placeholder: noLabel ? t('timeRegistration.field.description.label') : (timeRegistration.tickets?.length > 0 && timeRegistration.tickets.at(0).title),
                target: timeRegistration,
                name: 'description',
                onBlur: this.autosaveIfDescriptionChanged,
                autoComplete: 'off',
            },
            {
                Component: TargetDatePicker,
                includeWeeks: true,
                noLabel: noLabel,
                placeholder: startPlaceholder?.format(DATE_FORMAT) ?? (noLabel ? t('timeRegistration.field.date.label') : undefined),
                label: t('timeRegistration.field.date.label'),
                target: timeRegistration,
                name: 'startedAt',
                fromTarget: momentToLuxonNullSafe,
                toTarget: luxonToMomentNullSafe,
                value: timeRegistration.startedAt ?? this.startDate,
                onChange: this.setDate,
                autoComplete: 'off',
            },
            {
                Component: TargetTimePicker,
                noLabel: noLabel,
                noPopup: true,
                placeholder: startPlaceholder?.format(TIME_FORMAT) ?? (noLabel ? t('timeRegistration.field.startedAt.label') : undefined),
                target: timeRegistration,
                name: 'startedAt',
                onChange:this.setStartedAt,
                autoComplete: 'off',
            },
            {
                Component: TargetTimePicker,
                noLabel: noLabel,
                noPopup: true,
                placeholder: endPlaceholder?.format(TIME_FORMAT) ?? (noLabel ? t('timeRegistration.field.endedAt.label') : undefined),
                target: timeRegistration,
                name: 'endedAt',
                dateLib: 'moment',
                afterChange: this.autosave,
                autoComplete: 'off',
            },
        ];
    }

    renderFlex(fieldSettings) {
        const { children } = this.props;
        let descriptionFields = fieldSettings.slice(0,2);
        let timeFields = fieldSettings.slice(2);

        return <>
            {descriptionFields.map(field => <field.Component {...field}/>)}
            <FlexGroup>
                {/*{timeFields.map(field => <field.Component style={{width:'100%'}} {...field}/>)}*/}
                {timeFields.map(field => <field.Component {...field}/>)}
                {children}
            </FlexGroup>
        </>
    }

    renderInline(fieldSettings) {
        const { children } = this.props;
        let fields = fieldSettings.map(field => <field.Component {...field}/>)
        fields.push(children)
        return fields
    }

    render() {
        const { group, noLabel } = this.props;

        let fields = this.getSettings();
        fields = noLabel ? this.renderInline(fields): this.renderFlex(fields);


        if (group) {
            fields = (
                <Form.Group>{fields}</Form.Group>
            );
        }

        return (
            <Form>{fields}</Form>
        );
    }
}

function getMinEndedAt(now) {
    // We use 4 at night instead of 12 since its more common to work past 12 than it is to start before 4.
    // So this will give the expected behaviour more often.
    const minEndedAt = now.clone().set({ hour: 4, minute: 0, second: 0, millisecond: 0 });
    // Make sure that between 12 and 4 it refers to 4 the previous day
    if (minEndedAt.isAfter(now)) {
        minEndedAt.subtract(1, 'd');
    }
    return minEndedAt;
}

@observer
export class CurrentTimeRegistrationForm extends Component {
    static propTypes = {
        open: PropTypes.bool,
    };
    static defaultProps = {
        open: true,
    };

    constructor(...args) {
        super(...args);
        this.formRef = this.formRef.bind(this);
        this.startStop = this.startStop.bind(this);
    }

    form = null;

    formRef(node) {
        this.form = node;
    }

    async startStop() {
        const { currentUser, currentTimeRegistration } = window.viewStore;

        if (currentTimeRegistration.isNew && !currentTimeRegistration.startedAt) {
            // Handle new time registration.
            this.form.setStartedAt(this.startPlaceholder);
        } else if (!!currentTimeRegistration.startedAt && !currentTimeRegistration.endedAt) {
            // Handle open currentTimeRegistration.
            currentTimeRegistration.setInput('endedAt', this.endPlaceholder);
            await this.form.save();
            if (window.viewStore.currentUser.autoClearTimeRegistration) {
                runInAction(() => {
                    currentTimeRegistration.clear();
                    currentTimeRegistration.user.parse(currentUser.toJS());
                });
            }
        } else {
            runInAction(() => {
                currentTimeRegistration.clear();
                currentTimeRegistration.user.parse(currentUser.toJS());
            });

            const form = ReactDOM.findDOMNode(this.form);
            const subprojectsOrTicketsInput = form.querySelector('[name="subprojectsOrTickets"] input');
            setTimeout(() => subprojectsOrTicketsInput.select(), 0);
        }
    }

    @action onOpen() {
        window.viewStore.activeCurrentTimeRegistrationForms++;
    }

    @action onClose() {
        const timeRegistration = window.viewStore.currentTimeRegistration;
        if (--window.viewStore.activeCurrentTimeRegistrationForms === 0 && timeRegistration.endedAt !== null) {
            timeRegistration.clear();
            timeRegistration.user.parse(window.viewStore.currentUser.toJS());
        }
    }

    @observable minEndedAt = getMinEndedAt(window.viewStore.currentMinute);

    componentDidMount() {
        this.openReaction = reaction(
            () => this.props.open,
            (open) => {
                if (open) {
                    this.onOpen();
                } else {
                    this.onClose();
                }
            },
        );
        this.lastEndedAtReaction = reaction(
            () => window.viewStore.currentMinute,
            (now) => {
                const minEndedAt = getMinEndedAt(now);
                if (!this.minEndedAt.isSame(minEndedAt)) {
                    this.minEndedAt = minEndedAt;
                }
            },
            { fireImmediately: true },
        );

        if (this.props.open) {
            this.onOpen();
        }
    }

    componentWillUnmount() {
        this.openReaction();
        this.lastEndedAtReaction();

        if (this.props.open) {
            this.onClose();
        }
    }

    @computed get startPlaceholder() {
        if (window.viewStore.lastTimeRegistration.endedAt?.isSameOrAfter(this.minEndedAt)) {
            return window.viewStore.lastTimeRegistration.endedAt;
        } else {
            return window.viewStore.currentMinute;
        }
    }

    @computed get endPlaceholder() {
        if (window.viewStore.currentTimeRegistration.startedAt === null) {
            return null;
        } else {
            return window.viewStore.currentMinute;
        }
    }

    render() {
        const timeRegistration = window.viewStore.currentTimeRegistration;

        let icon, color;
        if (timeRegistration.startedAt === null) {
            icon = 'play';
            color = 'green';
        } else if (timeRegistration.endedAt === null) {
            icon = 'stop';
            color = 'red';
        } else {
            icon = 'level down rotated clockwise';
            color = 'grey';
        }

        return (
            <TimeRegistrationForm autosave group renderFlex
                timeRegistration={timeRegistration}
                ref={this.formRef}
                startPlaceholder={this.startPlaceholder}
                endPlaceholder={this.endPlaceholder}
                {...this.props}
            >
                <CenterIconButton data-test-start-stop
                    type="submit"
                    loading={timeRegistration.isLoading}
                    icon={icon}
                    color={color}
                    onClick={this.startStop}
                />
            </TimeRegistrationForm>
        );
    }
}
